<template>
  <div class="dashboard-container">
    <div class="sidebar-container">
      <Sidebar />
    </div>
    
    <div class="main-content">
      <header class="page-header">
        <div class="header-content">
          <h1 class="page-title">Categories</h1>
          <p class="page-description">Manage your course categories</p>
        </div>
        <div class="header-actions">
          <button class="btn-secondary export-btn" @click="exportCategories">
            <i class="fas fa-file-export"></i>
            <span>Export</span>
          </button>
          <button class="btn-primary add-category-btn" @click="openAddModal">
            <i class="fas fa-plus"></i>
            <span>Add Category</span>
          </button>
        </div>
      </header>

      <!-- Notification System -->
      <div class="notifications-container">
        <transition-group name="notification">
          <div v-if="error" key="error" class="notification is-error">
            <div class="notification-content">
              <i class="fas fa-exclamation-circle"></i>
              <p>{{ error }}</p>
            </div>
            <button @click="error = null" class="notification-close">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </transition-group>
      </div>

      <!-- Stats Cards Row -->
      <div class="stats-section">
        <div class="stat-card">
          <div class="stat-card-body">
            <div class="stat-icon">
              <i class="fas fa-folder"></i>
            </div>
            <div class="stat-details">
              <h3 class="stat-value">{{ categories.length }}</h3>
              <p class="stat-label">Total Categories</p>
            </div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-card-body">
            <div class="stat-icon">
              <i class="fas fa-book"></i>
            </div>
            <div class="stat-details">
              <h3 class="stat-value">{{ totalCourses }}</h3>
              <p class="stat-label">Total Courses</p>
            </div>
          </div>
        </div>

        <div class="stat-card">
          <div class="stat-card-body">
            <div class="stat-icon">
              <i class="fas fa-chart-bar"></i>
            </div>
            <div class="stat-details">
              <h3 class="stat-value">{{ averageCoursesPerCategory }}</h3>
              <p class="stat-label">Avg Courses/Category</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Search and Filters -->
      <div class="filters-section">
        <div class="search-wrapper">
          <div class="search-input-container">
            <i class="fas fa-search search-icon"></i>
            <input
              type="text"
              v-model="searchQuery"
              placeholder="Search categories..."
              class="search-input"
            />
            <button v-if="searchQuery" @click="searchQuery = ''" class="clear-search">
              <i class="fas fa-times"></i>
            </button>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-container">
        <div class="spinner"></div>
        <p>Loading categories...</p>
      </div>

      <!-- Categories Grid -->
      <div v-else-if="filteredCategories.length > 0" class="categories-grid">
        <div v-for="category in filteredCategories" :key="category.id" class="category-card">
          <div class="category-card-header">
            <div class="category-icon-wrapper">
              <i class="fas fa-folder category-icon"></i>
            </div>
            <div class="category-actions">
              <button class="btn-icon btn-edit" @click="openEditModal(category)" title="Edit">
                <i class="fas fa-edit"></i>
              </button>
              <button class="btn-icon btn-delete" @click="deleteCategory(category)" title="Delete">
                <i class="fas fa-trash-alt"></i>
              </button>
            </div>
          </div>
          <div class="category-card-body">
            <h3 class="category-title" v-html="formatText(category.title)"></h3>
            <div class="category-title-ar" v-html="formatText(category.title_ar)"></div>
            <div class="category-courses" @click="showCourses(category)" :class="{ 'has-courses': category.courses?.length > 0 }">
              <i class="fas fa-book-open"></i>
              <span>{{ category.courses?.length || 0 }} courses</span>
              <i v-if="category.courses?.length > 0" class="fas fa-chevron-right course-arrow"></i>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="empty-state-container">
        <div class="empty-state">
          <i class="fas fa-folder-open empty-icon"></i>
          <h3 class="empty-title">{{ searchQuery ? 'No matches found' : 'No categories yet' }}</h3>
          <p class="empty-description">
            {{ searchQuery ? 'Try adjusting your search or clear the filter' : 'Create your first category to start organizing your courses' }}
          </p>
          <div class="empty-actions">
            <button v-if="searchQuery" class="btn-secondary" @click="searchQuery = ''">
              <i class="fas fa-times"></i>
              <span>Clear search</span>
            </button>
            <button class="btn-primary" @click="openAddModal">
              <i class="fas fa-plus"></i>
              <span>{{ searchQuery ? 'Add new category' : 'Create first category' }}</span>
            </button>
          </div>
        </div>
      </div>

      <!-- Category Modal -->
      <CategoryModal
        :show="showModal"
        :category="selectedCategory"
        @close="showModal = false"
        @submit="handleSubmit"
      />

      <!-- Courses Modal -->
      <transition name="modal">
        <div v-if="showCoursesModal" class="modal-overlay" @click.self="showCoursesModal = false">
          <div class="courses-modal">
            <div class="modal-header">
              <h2>
                <i class="fas fa-folder"></i>
                {{ selectedCategory?.title }}
              </h2>
              <button class="close-button" @click="showCoursesModal = false">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <div class="modal-body">
              <div v-if="selectedCategory?.courses?.length > 0" class="courses-list">
                <div v-for="course in selectedCategory.courses" :key="course.id" class="course-item">
                  <div class="course-header">
                    <h3 class="course-title">{{ course.title }}</h3>
                    <div class="course-meta">
                      <span class="course-duration"><i class="fas fa-clock"></i> {{ course.minutes }} min</span>
                      <span v-if="course.is_premium" class="course-premium"><i class="fas fa-crown"></i> Premium</span>
                    </div>
                  </div>
                  <div class="course-info">
                    <div class="course-description" v-html="truncateDescription(course.description, 150)"></div>
                    <div class="course-arabic">
                      <strong>Arabic Title:</strong> {{ course.title_ar }}
                    </div>
                    <div class="course-category">
                      <strong>Category:</strong>
                      <div class="category-select-wrapper">
                        <select 
                          v-model="course.category_id" 
                          class="category-select"
                          @change="updateCourseCategory(course)"
                        >
                          <option v-for="cat in categories" :key="cat.id" :value="cat.id">
                            {{ cat.title }}
                          </option>
                        </select>
                        <div class="select-icon">
                          <i class="fas fa-chevron-down"></i>
                        </div>
                      </div>
                      <transition name="fade">
                        <span v-if="course.updating" class="updating-spinner"></span>
                        <span v-else-if="course.updateSuccess" class="update-success"><i class="fas fa-check"></i> Updated</span>
                        <span v-else-if="course.updateError" class="update-error"><i class="fas fa-exclamation-circle"></i> Failed</span>
                      </transition>
                    </div>
                    <div class="course-status">
                      <span class="status-badge" :class="{ 'is-active': course.is_active }">
                        {{ course.is_active ? 'Active' : 'Inactive' }}
                      </span>
                      <span v-if="course.is_breathing" class="breathing-badge">
                        <i class="fas fa-wind"></i> Breathing
                      </span>
                      <span v-if="course.top_course" class="top-badge">
                        <i class="fas fa-star"></i> Top Course
                      </span>
                    </div>
                    <div class="course-price" v-if="course.price">
                      <strong>Price:</strong> ${{ course.price }}
                    </div>
                  </div>
                </div>
              </div>
              <div v-else class="empty-courses">
                <i class="fas fa-book-open"></i>
                <p>No courses found for this category</p>
                <button class="btn-secondary" @click="showCoursesModal = false">Close</button>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import Sidebar from './Sidebar.vue';
import CategoryModal from './CategoryModal.vue';

export default {
  name: 'CategoriesList',
  components: {
    Sidebar,
    CategoryModal
  },
  data() {
    return {
      categories: [],
      loading: true,
      searchQuery: '',
      showModal: false,
      showCoursesModal: false,
      selectedCategory: null,
      error: null
    };
  },
  computed: {
    filteredCategories() {
      if (!this.searchQuery) return this.categories;
      const query = this.searchQuery.toLowerCase();
      return this.categories.filter(category => 
        category.title.toLowerCase().includes(query) ||
        category.title_ar.toLowerCase().includes(query)
      );
    },
    totalCourses() {
      return this.categories.reduce((total, category) => {
        // Safely handle categories without courses property
        const coursesCount = category.courses?.length || 0;
        return total + coursesCount;
      }, 0);
    },
    averageCoursesPerCategory() {
      if (this.categories.length === 0) return 0;
      return (this.totalCourses / this.categories.length).toFixed(1);
    }
  },
  methods: {
    async fetchCategories() {
      try {
        this.loading = true;
        this.error = null;
        const token = localStorage.getItem('token');
        const response = await axios.get('https://api.kewaar.com/api/admin/coursecategories2', {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        this.categories = response.data.data;
      } catch (error) {
        this.error = 'Failed to fetch categories. Please try again.';
        console.error('Error fetching categories:', error);
      } finally {
        this.loading = false;
      }
    },

    exportCategories() {
      try {
        // Create a formatted version of the data to export
        const exportData = this.categories.map(category => {
          return {
            id: category.id,
            title: category.title,
            title_ar: category.title_ar,
            is_active: category.is_active ? 'Yes' : 'No',
            courses_count: category.courses?.length || 0
          };
        });
        
        // Convert to CSV
        const csvContent = this.convertToCSV(exportData);
        
        // Create download link
        const encodedUri = encodeURI('data:text/csv;charset=utf-8,' + csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', `categories_export_${new Date().toISOString().slice(0,10)}.csv`);
        document.body.appendChild(link);
        
        // Trigger download
        link.click();
        
        // Clean up
        document.body.removeChild(link);
      } catch (error) {
        this.error = 'Failed to export categories. Please try again.';
        console.error('Error exporting categories:', error);
      }
    },
    
    convertToCSV(objArray) {
      // Get the headers from the first object
      const headers = Object.keys(objArray[0] || {});
      
      // Create CSV header row
      const csvRows = [
        headers.join(',')
      ];
      
      // Add data rows
      for (const obj of objArray) {
        const values = headers.map(header => {
          const value = obj[header];
          // Handle special characters that might break CSV format
          return `"${String(value).replace(/"/g, '""')}"`;
        });
        csvRows.push(values.join(','));
      }
      
      return csvRows.join('\n');
    },

    openAddModal() {
      this.selectedCategory = null;
      this.showModal = true;
    },

    openEditModal(category) {
      this.selectedCategory = { ...category };
      this.showModal = true;
    },

    showCourses(category) {
      if (category.courses?.length > 0) {
        this.selectedCategory = { ...category };
        this.showCoursesModal = true;
      }
    },

    truncateDescription(description, length) {
      if (!description) return '';
      const stripHtml = description.replace(/<[^>]*>?/gm, '');
      if (stripHtml.length <= length) return description;
      return stripHtml.substring(0, length) + '...';
    },

    async updateCourseCategory(course) {
      try {
        // Set updating flag
        course.updating = true;
        course.updateSuccess = false;
        course.updateError = false;
        
        const token = localStorage.getItem('token');
        
        await axios.request({
          method: 'put',
          url: `https://api.kewaar.com/api/admin/courses/${course.id}`,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          data: JSON.stringify({
            category_id: course.category_id
          })
        });
        
        // If successful, we need to update our local data
        const oldCategoryId = this.selectedCategory.id;
        
        if (course.category_id !== oldCategoryId) {
          // Remove course from current category
          const currentCategoryIndex = this.categories.findIndex(c => c.id === oldCategoryId);
          if (currentCategoryIndex !== -1) {
            this.categories[currentCategoryIndex].courses = 
              this.categories[currentCategoryIndex].courses.filter(c => c.id !== course.id);
          }
          
          // Add course to new category
          const newCategoryIndex = this.categories.findIndex(c => c.id === course.category_id);
          if (newCategoryIndex !== -1) {
            if (!this.categories[newCategoryIndex].courses) {
              this.categories[newCategoryIndex].courses = [];
            }
            this.categories[newCategoryIndex].courses.push({...course});
          }
        }
        
        // Show success indicator
        course.updateSuccess = true;
        
        // Close modal after a short delay to show the success message
        setTimeout(() => {
          if (this.selectedCategory.courses.length === 1) {
            this.showCoursesModal = false;
          } else {
            // Remove this course from the displayed list
            this.selectedCategory.courses = this.selectedCategory.courses.filter(c => c.id !== course.id);
          }
        }, 1500);
        
      } catch (error) {
        console.error('Error updating course category:', error);
        course.updateError = true;
      } finally {
        course.updating = false;
      }
    },

    async handleSubmit(formData) {
      if (this.loading) return; // Prevent double submission
      
      try {
        this.loading = true;
        this.error = null;
        const token = localStorage.getItem('token');
        const isEditing = !!this.selectedCategory;
        const url = isEditing 
          ? `https://api.kewaar.com/api/admin/coursecategories2/${this.selectedCategory.id}`
          : 'https://api.kewaar.com/api/admin/coursecategories2';
        
        // Prepare the data in the format expected by the API
        const data = JSON.stringify({
          title: formData.title || 'test',
          title_ar: formData.title_ar || 'test',
          image_url: formData.image_url || 'test',
          image_url_ar: formData.image_url_ar || 'test_ar',
          is_active: formData.is_active ? 1 : 0
        });

        const config = {
          method: isEditing ? 'put' : 'post',
          maxBodyLength: Infinity,
          url: url,
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
            },
          data: data
        };

        const response = await axios.request(config);

        // Update local state
        if (isEditing) {
          const index = this.categories.findIndex(c => c.id === this.selectedCategory.id);
          if (index !== -1) {
            this.categories[index] = {
              ...this.categories[index],
              ...response.data.data,
              courses: this.categories[index].courses || []
            };
          }
        } else {
          this.categories.push({
            ...response.data.data,
            courses: []
          });
        }

        this.showModal = false;
        this.selectedCategory = null;
        return response; // Return response for the modal to handle
      } catch (error) {
        console.error('Error submitting category:', error);
        if (error.response) {
          // Extract validation errors if they exist
          const errorMessage = error.response.data.message || 
                             (error.response.data.errors ? Object.values(error.response.data.errors).flat().join(', ') : null) ||
                             'An error occurred while saving the category';
          this.error = errorMessage;
        } else if (error.request) {
          this.error = 'No response received from server. Please check your connection.';
        } else {
          this.error = error.message || 'An unexpected error occurred';
        }
        throw error;
      } finally {
        this.loading = false;
      }
    },

    async deleteCategory(category) {
      if (!confirm(`Are you sure you want to delete "${category.title}"?`)) return;

      try {
        this.error = null;
        const token = localStorage.getItem('token');
        await axios.delete(`https://api.kewaar.com/api/admin/coursecategories2/${category.id}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        // Remove from local state
        this.categories = this.categories.filter(c => c.id !== category.id);
      } catch (error) {
        this.error = 'Failed to delete category. Please try again.';
        console.error('Error deleting category:', error);
      }
    },

    async deleteCourse(courseId) {
      try {
        this.error = null;
        const token = localStorage.getItem('token');
        await axios.delete(`https://api.kewaar.com/api/admin/course2-categories/${courseId}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Cookie': '__cf_bm=AwoB2nMbr7TnyPAZt0IV2K9poXH9skz_6jejDAWyVV0-1740035970-1.0.1.1-_IxuPmGG9FusyojC2d9Sl7FwWvDfWAJXT1yGNr6FbFMYX3EheWJL.DMydi2K8VL2WFcf1NXP9ELPD6XMBEwPOQ'
          }
        });

        // Remove the course from local state
        this.categories = this.categories.filter(c => c.id !== courseId);
      } catch (error) {
        this.error = 'Failed to delete category. Please try again.';
        console.error('Error deleting category:', error);
      }
    },

    formatText(text) {
      if (!text) return '';
      return text
        .replace(/\n/g, '<br>')
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    }
  },
  created() {
    this.fetchCategories();
  }
};
</script>

<style scoped>
/* Base Styles */
.dashboard-container {
  display: grid;
  grid-template-columns: 250px 1fr;
  min-height: 100vh;
  background-color: #f8fafc;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}

.sidebar-container {
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
  z-index: 10;
  background-color: #fff;
}

.main-content {
  padding: 2rem;
  overflow-x: hidden;
}

/* Page Header */
.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.header-content {
  flex: 1;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
}

.export-btn {
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
}

.export-btn:hover {
  background-color: #f1f5f9;
  border-color: #cbd5e1;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: #0f172a;
  margin-bottom: 0.5rem;
  letter-spacing: -0.025em;
}

.page-description {
  color: #64748b;
  font-size: 0.875rem;
}

.add-category-btn {
  box-shadow: 0 4px 6px -1px rgba(59, 130, 246, 0.2);
}

/* Notification System */
.notifications-container {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 100;
  width: 100%;
  max-width: 400px;
}

.notification {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.5rem;
  border-radius: 0.5rem;
  margin-bottom: 0.75rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  animation: slideIn 0.3s ease-out;
}

.notification.is-error {
  background-color: #fee2e2;
  border-left: 4px solid #ef4444;
}

.notification-content {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.notification-content i {
  color: #ef4444;
  font-size: 1.25rem;
}

.notification-close {
  background: transparent;
  border: none;
  color: #6b7280;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-close:hover {
  background-color: rgba(0, 0, 0, 0.05);
}

/* Notification Animations */
.notification-enter-active {
  animation: slideIn 0.3s ease-out;
}

.notification-leave-active {
  animation: slideOut 0.3s ease-out;
}

@keyframes slideIn {
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
}

@keyframes slideOut {
  from { transform: translateX(0); opacity: 1; }
  to { transform: translateX(100%); opacity: 0; }
}

/* Stats Section */
.stats-section {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.stat-card {
  background-color: white;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.stat-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.stat-card-body {
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.stat-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 12px;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  color: white;
  font-size: 1.25rem;
}

.stat-card:nth-child(2) .stat-icon {
  background: linear-gradient(135deg, #10b981, #047857);
}

.stat-card:nth-child(3) .stat-icon {
  background: linear-gradient(135deg, #8b5cf6, #6d28d9);
}

.stat-details {
  flex: 1;
}

.stat-value {
  font-size: 1.75rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0 0 0.25rem;
  letter-spacing: -0.025em;
}

.stat-label {
  color: #64748b;
  font-size: 0.875rem;
  margin: 0;
}

/* Filters Section */
.filters-section {
  margin-bottom: 2rem;
}

.search-wrapper {
  width: 100%;
  max-width: 400px;
}

.search-input-container {
  position: relative;
  width: 100%;
}

.search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
}

.clear-search {
  position: absolute;
  right: 1rem;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #94a3b8;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
}

.clear-search:hover {
  color: #64748b;
  background-color: rgba(0, 0, 0, 0.05);
}

.search-input {
  width: 100%;
  padding: 0.75rem 2.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background-color: white;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.search-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

/* Button Styles */
.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #3b82f6;
  color: white;
  font-weight: 500;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.btn-primary:hover {
  background-color: #2563eb;
  transform: translateY(-1px);
}

.btn-secondary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background-color: white;
  color: #4b5563;
  font-weight: 500;
  padding: 0.75rem 1.5rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-secondary:hover {
  background-color: #f8fafc;
  border-color: #cbd5e1;
  transform: translateY(-1px);
}

/* Loading State */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem;
  background-color: white;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e2e8f0;
  border-radius: 50%;
  border-top-color: #3b82f6;
  animation: spin 1s linear infinite;
  margin-bottom: 1rem;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Categories Grid */
.categories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1.5rem;
}

.category-card {
  background-color: white;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.2s ease;
}

.category-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

.category-card-header {
  padding: 1.5rem 1.5rem 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.category-icon-wrapper {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: linear-gradient(135deg, #3b82f6, #1d4ed8);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.25rem;
}

.category-actions {
  display: flex;
  gap: 0.5rem;
}

.btn-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
}

.btn-edit {
  background-color: #e0f2fe;
  color: #0284c7;
}

.btn-edit:hover {
  background-color: #bae6fd;
  transform: translateY(-1px);
}

.btn-delete {
  background-color: #fee2e2;
  color: #ef4444;
}

.btn-delete:hover {
  background-color: #fecaca;
  transform: translateY(-1px);
}

.category-card-body {
  padding: 1.5rem;
}

.category-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 0.5rem;
}

.category-title-ar {
  direction: rtl;
  text-align: right;
  color: #64748b;
  font-size: 0.875rem;
  margin-bottom: 1rem;
}

.category-courses {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 0.5rem;
  background-color: #f1f5f9;
  color: #64748b;
  font-size: 0.875rem;
  transition: all 0.2s ease;
}

.category-courses.has-courses {
  cursor: pointer;
  background-color: #dbeafe;
  color: #1e40af;
}

.category-courses.has-courses:hover {
  background-color: #bfdbfe;
}

.course-arrow {
  margin-left: auto;
  font-size: 0.75rem;
  transition: transform 0.2s ease;
}

.category-courses.has-courses:hover .course-arrow {
  transform: translateX(3px);
}

/* Empty State */
.empty-state-container {
  display: flex;
  justify-content: center;
  padding: 2rem 0;
}

.empty-state {
  max-width: 500px;
  background-color: white;
  border-radius: 0.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 3rem 2rem;
  text-align: center;
}

.empty-icon {
  font-size: 3rem;
  color: #cbd5e1;
  margin-bottom: 1.5rem;
}

.empty-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0 0 0.5rem;
}

.empty-description {
  color: #64748b;
  margin-bottom: 1.5rem;
}

.empty-actions {
  display: flex;
  justify-content: center;
  gap: 1rem;
}

/* Modal Styling */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 100;
  padding: 1rem;
  overflow-y: auto;
}

.courses-modal {
  background-color: white;
  border-radius: 0.75rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  width: 100%;
  max-width: 800px;
  max-height: 80vh;
  overflow-y: auto;
  animation: modalFadeIn 0.3s ease-out;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #e2e8f0;
}

.modal-header h2 {
  margin: 0;
  font-size: 1.25rem;
  color: #0f172a;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.modal-header h2 i {
  color: #3b82f6;
}

.close-button {
  background: transparent;
  border: none;
  color: #64748b;
  cursor: pointer;
  width: 2rem;
  height: 2rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.close-button:hover {
  background-color: #f1f5f9;
  color: #0f172a;
}

.modal-body {
  padding: 1.5rem;
}

/* Modal Animations */
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
}

@keyframes modalFadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Courses List */
.courses-list {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1rem;
}

.course-item {
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  overflow: hidden;
  transition: all 0.2s ease;
}

.course-item:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border-color: #cbd5e1;
}

.course-header {
  padding: 1rem;
  background-color: #f8fafc;
  border-bottom: 1px solid #e2e8f0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.course-title {
  margin: 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #0f172a;
}

.course-meta {
  display: flex;
  gap: 0.75rem;
  align-items: center;
}

.course-duration {
  font-size: 0.75rem;
  color: #64748b;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.course-premium {
  font-size: 0.75rem;
  color: #d97706;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.course-info {
  padding: 1rem;
}

.course-description {
  margin-bottom: 1rem;
  color: #4b5563;
  font-size: 0.875rem;
  line-height: 1.5;
}

.course-arabic {
  margin-bottom: 1rem;
  color: #4b5563;
  font-size: 0.875rem;
}

.course-category {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.category-select-wrapper {
  position: relative;
  display: inline-block;
}

.category-select {
  appearance: none;
  background-color: #f8fafc;
  border: 1px solid #e2e8f0;
  padding: 0.375rem 2rem 0.375rem 0.75rem;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  color: #0f172a;
  cursor: pointer;
  transition: all 0.2s ease;
}

.category-select:hover {
  border-color: #cbd5e1;
}

.category-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.select-icon {
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #64748b;
  font-size: 0.75rem;
}

.updating-spinner {
  display: inline-block;
  width: 1rem;
  height: 1rem;
  border: 2px solid #e2e8f0;
  border-radius: 50%;
  border-top-color: #3b82f6;
  animation: spin 1s linear infinite;
}

.update-success {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: #047857;
  font-size: 0.75rem;
}

.update-error {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  color: #ef4444;
  font-size: 0.75rem;
}

.course-status {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
  background-color: #f1f5f9;
  color: #64748b;
}

.status-badge.is-active {
  background-color: #d1fae5;
  color: #047857;
}

.breathing-badge, .top-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0.5rem;
  border-radius: 0.25rem;
  font-size: 0.75rem;
  font-weight: 500;
}

.breathing-badge {
  background-color: #e0f2fe;
  color: #0284c7;
}

.top-badge {
  background-color: #fef3c7;
  color: #d97706;
}

.course-price {
  margin-top: 1rem;
  font-size: 0.875rem;
  color: #4b5563;
}

.empty-courses {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  color: #64748b;
  text-align: center;
}

.empty-courses i {
  font-size: 2.5rem;
  color: #cbd5e1;
  margin-bottom: 1rem;
}

.empty-courses p {
  margin-bottom: 1.5rem;
}

/* Fade Animation */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Responsive Adjustments */
@media (max-width: 1024px) {
  .dashboard-container {
    grid-template-columns: 200px 1fr;
  }
  
  .main-content {
    padding: 1.5rem;
  }
  
  .categories-grid {
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  }
}

@media (max-width: 768px) {
  .dashboard-container {
    grid-template-columns: 1fr;
  }
  
  .sidebar-container {
    display: none;
  }
  
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }
  
  .header-actions {
    width: 100%;
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .add-category-btn, .export-btn {
    width: 100%;
    justify-content: center;
  }
  
  .stats-section {
    grid-template-columns: 1fr;
  }
  
  .categories-grid {
    grid-template-columns: 1fr;
  }

  .courses-modal {
    max-width: 95%;
    max-height: 90vh;
  }

  .course-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .course-meta {
    width: 100%;
    justify-content: flex-start;
  }
  
  .empty-actions {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .empty-actions button {
    width: 100%;
    justify-content: center;
  }
}
</style> 